import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit3, Trash2, Save, X, CheckCircle, AlertCircle, Award, User, Target } from 'lucide-react';
import api from '../utils/api';


const UserDomainSpecialtiesSettings = ({ user }) => {
  const [domains, setDomains] = useState([]);
  const [userAssignments, setUserAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null); // 'domains' or 'assignments'
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Form state for domain creation/editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'technical',
    examples: [],
    required_skills: [],
    proficiency_levels: ['beginner', 'intermediate', 'advanced', 'expert'],
    confidence_threshold: 0.7,
    user_id: '',
    proficiency_level: 'intermediate'
  });

  // Assignment form state
  const [assignmentData, setAssignmentData] = useState({
    user_id: '',
    domain_assignments: [],
    fallback_preference: false
  });

  const [availableUsers, setAvailableUsers] = useState([]);

  useEffect(() => {
    syncCategoriesAndFetch();
  }, []);

  const syncCategoriesAndFetch = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // First sync categories to domains
      await api.post(`/task-categories/sync-to-domains`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Then fetch everything
      await fetchDomains();
      await fetchUserAssignments();
      await fetchUsers();
    } catch (error) {
      console.error('Failed to sync categories:', error);
      // Continue even if sync fails
      fetchDomains();
      fetchUserAssignments();
      fetchUsers();
    }
  };

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/user-domains`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDomains(response.data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      showNotification('error', 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssignments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/user-domain-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch user assignments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAvailableUsers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'technical',
      examples: [],
      required_skills: [],
      proficiency_levels: ['beginner', 'intermediate', 'advanced', 'expert'],
      confidence_threshold: 0.7,
      user_id: '',
      proficiency_level: 'intermediate'
    });
    setEditingDomain(null);
  };

  const handleAddDomain = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditDomain = (domain) => {
    setFormData({
      name: domain.name || '',
      description: domain.description || '',
      category: domain.category || 'technical',
      examples: domain.examples || [],
      required_skills: domain.required_skills || [],
      proficiency_levels: domain.proficiency_levels || ['beginner', 'intermediate', 'advanced', 'expert'],
      confidence_threshold: domain.confidence_threshold || 0.7
    });
    setEditingDomain(domain);
    setShowAddModal(true);
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain? This will remove all user assignments to this domain.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/user-domains/${domainId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showNotification('success', 'Domain deleted successfully');
      fetchDomains();
      fetchUserAssignments();
    } catch (error) {
      console.error('Failed to delete domain:', error);
      showNotification('error', 'Failed to delete domain');
    }
  };

  const handleSaveDomain = async () => {
    if (!formData.name.trim()) {
      showNotification('error', 'Domain name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const domainData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        examples: formData.examples,
        required_skills: formData.required_skills,
        proficiency_levels: formData.proficiency_levels,
        confidence_threshold: formData.confidence_threshold
      };

      if (editingDomain) {
        await api.put(`/user-domains/${editingDomain.id}`, domainData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        showNotification('success', 'Domain updated successfully');
      } else {
        await api.post(`/user-domains`, domainData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        showNotification('success', 'Domain created successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchDomains();
    } catch (error) {
      console.error('Failed to save domain:', error);
      showNotification('error', 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const updateUserAssignment = async (userId, domainId, proficiency, isPrimary = false) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/user-domain-assignments/${userId}/${domainId}`, {
        proficiency_level: proficiency,
        is_primary_specialty: isPrimary
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchUserAssignments();
      showNotification('success', 'User assignment updated successfully');
    } catch (error) {
      console.error('Failed to update assignment:', error);
      showNotification('error', 'Failed to update user assignment');
    }
  };

  const removeUserAssignment = async (userId, domainId) => {
    if (!window.confirm('Remove this domain assignment from the user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/user-domain-assignments/${userId}/${domainId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchUserAssignments();
      showNotification('success', 'User assignment removed successfully');
    } catch (error) {
      console.error('Failed to remove assignment:', error);
      showNotification('error', 'Failed to remove user assignment');
    }
  };
  const handleSaveAssignment = async () => {
    if (!formData.user_id || !selectedDomain) {
      showNotification('error', 'Please select a user');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.post(`/user-domain-assignments`, {
        user_id: formData.user_id,
        domain_id: selectedDomain.id,
        proficiency_level: formData.proficiency_level
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      resetForm();
      fetchUserAssignments();
      showNotification('success', 'User assigned successfully');
    } catch (error) {
      console.error('Failed to assign user:', error);
      showNotification('error', 'Failed to assign user to domain');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this user from the domain?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/user-domain-assignments/${assignmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchUserAssignments();
      showNotification('success', 'User removed from domain');
    } catch (error) {
      console.error('Failed to remove assignment:', error);
      showNotification('error', 'Failed to remove user assignment');
    }
  };

  const addExample = (example) => {
    if (example.trim() && !formData.examples.includes(example.trim())) {
      setFormData(prev => ({
        ...prev,
        examples: [...prev.examples, example.trim()]
      }));
    }
  };

  const removeExample = (exampleToRemove) => {
    setFormData(prev => ({
      ...prev,
      examples: prev.examples.filter(example => example !== exampleToRemove)
    }));
  };

  const addSkill = (skill) => {
    if (skill.trim() && !formData.required_skills.includes(skill.trim())) {
      setFormData(prev => ({
        ...prev,
        required_skills: [...prev.required_skills, skill.trim()]
      }));
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      required_skills: prev.required_skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const getProficiencyColor = (level) => {
    switch (level) {
      case 'expert': return 'bg-purple-100 text-purple-800';
      case 'advanced': return 'bg-blue-100 text-blue-800';
      case 'intermediate': return 'bg-green-100 text-green-800';
      case 'beginner': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Domains with User Assignments */}
        <div className="space-y-6">
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Expertise domains are automatically synced from your Task Categories. 
                To add or edit domains, go to <strong>Settings → Task Categories</strong>.
              </p>
            </div>

            {/* Domains List */}
            {domains.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="mb-2">
                          <h5 className="font-black text-black">{domain.name}</h5>
                        </div>
                        <p className="text-sm text-gray-800 mb-3">{domain.description}</p>
                        
                        {domain.examples?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-700 mb-1">Example Tasks:</p>
                            <div className="flex flex-wrap gap-1">
                              {domain.examples.slice(0, 3).map((example, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {example}
                                </span>
                              ))}
                              {domain.examples.length > 3 && (
                                <span className="text-xs text-gray-500">+{domain.examples.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Assigned Users */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Assigned Users:</span>
                            <button
                              onClick={() => setSelectedDomain(domain)}
                              className="flex items-center space-x-1 px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                            >
                              <Users className="w-3 h-3" />
                              <span>Assign User</span>
                            </button>
                          </div>
                          {userAssignments.filter(a => a.domain_id === domain.id).length > 0 ? (
                            <div className="space-y-2">
                              {userAssignments.filter(a => a.domain_id === domain.id).map((assignment) => (
                                <div key={assignment.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-gray-200">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                      <span className="text-white font-bold text-xs">
                                        {assignment.user_name?.split(' ').map(n => n[0]).join('') || 'U'}
                                      </span>
                                    </div>
                                    <span className="text-sm text-gray-900">{assignment.user_name}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 rounded-full text-xs ${
                                      assignment.proficiency_level === 'expert' ? 'bg-green-100 text-green-800' :
                                      assignment.proficiency_level === 'advanced' ? 'bg-blue-100 text-blue-800' :
                                      assignment.proficiency_level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {assignment.proficiency_level}
                                    </span>
                                    <button
                                      onClick={() => handleDeleteAssignment(assignment.id)}
                                      className="text-red-500 hover:text-red-700"
                                      title="Remove assignment"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No users assigned yet</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Expertise Domains</h3>
                <p className="text-gray-600 mb-4">
                  Expertise domains are automatically synced from your Task Categories. 
                  Create categories in <strong>Settings → Task Categories</strong> to enable intelligent task assignment.
                </p>
              </div>
            )}
          </div>

          {/* Assignment Modal */}
          {selectedDomain && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedDomain(null)}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Assign User to {selectedDomain.name}
                  </h3>
                  <button
                    onClick={() => setSelectedDomain(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select User
                    </label>
                    <select
                      value={formData.user_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a user...</option>
                      {availableUsers.map((member) => (
                        <option key={member.user.id} value={member.user.id}>
                          {member.user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proficiency Level
                    </label>
                    <select
                      value={formData.proficiency_level}
                      onChange={(e) => setFormData(prev => ({ ...prev, proficiency_level: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setSelectedDomain(null)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleSaveAssignment();
                      setSelectedDomain(null);
                    }}
                    disabled={!formData.user_id}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400"
                  >
                    Assign User
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Old User Assignments Section and Domain Modal Removed - Domains are now read-only and synced from categories */}
    </>
  );
};

export default UserDomainSpecialtiesSettings;
